export type PatientAgeUnit = 'سنة' | 'شهر' | 'يوم';

export interface NewPatientEntry {
  name: string;
  phone: string;
  age: string;
  ageUnit: PatientAgeUnit;
  nationality: string;
  address: string;
}

export type NewPatientEntryErrors = Partial<Record<keyof NewPatientEntry, string>>;

const INTERNATIONAL_CODES = [
  '20', '966', '971', '965', '974', '973', '968', '962', '961', '963', '964',
  '249', '218', '216', '213', '212', '970', '1', '44', '33', '49', '39',
];

export const normalizePatientPhoneInput = (value: string): string => value
  .replace(/[٠-٩]/g, digit => String('٠١٢٣٤٥٦٧٨٩'.indexOf(digit)))
  .replace(/[۰-۹]/g, digit => String('۰۱۲۳۴۵۶۷۸۹'.indexOf(digit)))
  .replace(/[^\d+]/g, '')
  .replace(/(?!^)\+/g, '')
  .slice(0, 16);

export const phoneDigits = (value: string): string => normalizePatientPhoneInput(value).replace(/\D/g, '');

export function getPatientPhoneValidationError(value: string): string | undefined {
  const normalized = normalizePatientPhoneInput(value);
  const digits = phoneDigits(normalized);
  if (!digits) return 'Phone number is required / رقم الهاتف مطلوب';
  if (/^01[0125]\d{8}$/.test(digits)) return undefined;
  if (/^09\d{8}$/.test(digits) || /^05\d{8}$/.test(digits) || /^05[024568]\d{7}$/.test(digits)) return undefined;

  const international = normalized.startsWith('+') ? digits : (digits.startsWith('00') ? digits.slice(2) : '');
  if (international && international.length >= 8 && international.length <= 15 &&
      INTERNATIONAL_CODES.some(code => international.startsWith(code))) return undefined;

  return 'Enter a valid mobile number, e.g. 01012345678 / أدخل رقم هاتف صحيح';
}

export function validateNewPatientEntry(input: NewPatientEntry): {
  errors: NewPatientEntryErrors;
  value: NewPatientEntry;
} {
  const value: NewPatientEntry = {
    name: input.name.trim().replace(/\s+/g, ' '),
    phone: normalizePatientPhoneInput(input.phone),
    age: input.age.trim(),
    ageUnit: input.ageUnit,
    nationality: input.nationality.trim().replace(/\s+/g, ' '),
    address: input.address.trim().replace(/\s+/g, ' '),
  };
  const errors: NewPatientEntryErrors = {};

  if (!value.name) errors.name = 'Patient name is required / اسم المريض مطلوب';
  else if (value.name.length < 2) errors.name = 'Patient name must contain at least 2 characters';
  else if (value.name.length > 120) errors.name = 'Patient name must not exceed 120 characters';
  else if (!/[A-Za-z\u0600-\u06FF]/.test(value.name)) errors.name = 'Enter a valid patient name / أدخل اسماً صحيحاً';

  const phoneError = getPatientPhoneValidationError(value.phone);
  if (phoneError) errors.phone = phoneError;

  if (value.age) {
    const age = Number(value.age);
    const maximum = value.ageUnit === 'سنة' ? 130 : value.ageUnit === 'شهر' ? 1560 : 47450;
    if (!Number.isInteger(age) || age <= 0) errors.age = 'Age must be a positive whole number / العمر يجب أن يكون رقماً صحيحاً';
    else if (age > maximum) errors.age = 'Age is outside the supported range / العمر خارج النطاق المسموح';
  }
  if (value.nationality.length > 100) errors.nationality = 'Nationality must not exceed 100 characters';
  if (value.address.length > 500) errors.address = 'Address must not exceed 500 characters';

  return { errors, value };
}
